import {
  QuotaExceededError,
  type CollectionId,
  type NewRecipe,
  type RecipeId,
  type RecipeStore,
} from '@platewell/core';
import type { ShareRecipe } from '@platewell/schema';
import { importShare } from './api.js';

export interface ShareImportResult {
  collectionId: CollectionId;
  collectionName: string;
  /** Recipes in the share, whether or not there was room for them. */
  total: number;
  /** The new ids, in manifest order — the copies, not the sender's originals. */
  recipeIds: RecipeId[];
  /** Set when the plan ran out of room part-way; `recipeIds` holds what did fit. */
  blockedBy: QuotaExceededError | null;
}

/**
 * Saves a shared link straight into the household's import collection.
 *
 * There is no destination to choose: a link arrives from outside the app, usually on a
 * phone, and the recipes are just as easy to move afterwards as they would have been to
 * file on the way in. The server hands back the manifest and grants the photo blobs; the
 * ids are minted here, which is what makes the copy the recipient's own.
 */
export async function saveSharedRecipes(
  store: RecipeStore,
  householdId: string,
  token: string,
): Promise<ShareImportResult> {
  const { manifest } = await importShare(token, { householdId });
  const collectionId = store.getImportCollectionId();

  const recipeIds: RecipeId[] = [];
  for (const recipe of manifest.recipes) {
    try {
      recipeIds.push(await store.addRecipe(toNewRecipe(recipe), [collectionId]));
    } catch (cause) {
      // A full household keeps what fitted rather than losing the lot — the same bargain
      // the URL importer strikes when a batch runs past the cap.
      if (cause instanceof QuotaExceededError) {
        return {
          ...base(store, collectionId, manifest.recipes.length, recipeIds),
          blockedBy: cause,
        };
      }
      throw cause;
    }
  }

  return { ...base(store, collectionId, manifest.recipes.length, recipeIds), blockedBy: null };
}

function base(
  store: RecipeStore,
  collectionId: CollectionId,
  total: number,
  recipeIds: RecipeId[],
): Omit<ShareImportResult, 'blockedBy'> {
  return {
    collectionId,
    collectionName: store.getCollection(collectionId)?.name ?? 'Main',
    total,
    recipeIds,
  };
}

/**
 * The manifest carries no ids by design, so this is a plain field copy. Photos come across
 * as blob references: the import call already granted this household access to them.
 */
function toNewRecipe(recipe: ShareRecipe): NewRecipe {
  return {
    title: recipe.title,
    description: recipe.description,
    sourceType: recipe.sourceType,
    sourceUrl: recipe.sourceUrl,
    sourceText: recipe.sourceText,
    servings: recipe.servings,
    prepMinutes: recipe.prepMinutes,
    cookMinutes: recipe.cookMinutes,
    notes: recipe.notes,
    tags: recipe.tags,
    ingredients: recipe.ingredients,
    instructions: recipe.instructions,
    photos: recipe.photos,
    nutritionBasis: recipe.nutritionBasis,
    nutritionFacts: recipe.nutritionFacts,
  };
}

/**
 * What the toast says once the copy has landed.
 *
 * A partial save names both numbers: the recipes that are missing were the plan's doing,
 * not the link's, and the collection the user is now looking at does not say so itself.
 */
export function shareSavedMessage(result: ShareImportResult): string {
  const { total, collectionName } = result;
  const imported = result.recipeIds.length;
  if (imported < total) {
    return `Saved ${imported} of ${total} recipes to ${collectionName} \u2014 your plan is full`;
  }
  const noun = imported === 1 ? 'Recipe' : `${imported} recipes`;
  return `${noun} saved to ${collectionName}`;
}
